import { useState } from "react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SignaturePad } from "@/components/SignaturePad";
import { FileCheck2, FileDown, Lock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Field, TInput } from "@/components/technique/fields";

const LABEL = { pre: "Pré-réception", definitive: "Réception définitive" };

function PvDialog({ type, file, openReserves, onClose, onDone }) {
  const [f, setF] = useState({ type, date: new Date().toISOString().slice(0, 10), participants: "", observations: "", sig_client: "", sig_client_nom: file.client_name || "", sig_tech: "", sig_tech_nom: file.responsable_name || "" });
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const ok = f.sig_client && f.sig_tech && f.sig_client_nom.trim() && f.sig_tech_nom.trim();
  const save = async () => {
    try { await api.post(`/technique/files/${file.id}/receptions`, f); toast.success(`${LABEL[type]} enregistrée`); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="pv-dialog">
        <DialogHeader><DialogTitle>PV de {LABEL[type].toLowerCase()}</DialogTitle></DialogHeader>
        {openReserves > 0 && <p className="text-sm rounded-md bg-amber-50 text-amber-800 px-3 py-2"><AlertTriangle className="inline h-4 w-4 mr-1" />{openReserves} réserve(s) ouverte(s) : le PV sera établi <b>avec réserves</b>.</p>}
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Date"><TInput type="date" value={f.date} onChange={set("date")} testid="pv-date" /></Field>
          <Field label="Participants"><TInput value={f.participants} onChange={set("participants")} testid="pv-participants" /></Field>
          <Field label="Observations" className="sm:col-span-2"><Textarea rows={2} value={f.observations} onChange={(e) => set("observations")(e.target.value)} data-testid="pv-observations" /></Field>
          <div data-testid="pv-sig-client"><Field label="Nom du client signataire"><TInput value={f.sig_client_nom} onChange={set("sig_client_nom")} testid="pv-client-nom" /></Field><div className="mt-2"><SignaturePad value={f.sig_client} onChange={set("sig_client")} height={130} /></div></div>
          <div data-testid="pv-sig-tech"><Field label="Responsable technique signataire"><TInput value={f.sig_tech_nom} onChange={set("sig_tech_nom")} testid="pv-tech-nom" /></Field><div className="mt-2"><SignaturePad value={f.sig_tech} onChange={set("sig_tech")} height={130} /></div></div>
        </div>
        <DialogFooter><Button disabled={!ok} onClick={save} data-testid="pv-save" className="bg-brand-600 hover:bg-brand-700 text-white">Signer et enregistrer le PV</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloseBox({ file, openReserves, hasDefinitive, reload }) {
  const { role } = useAuth();
  const [motif, setMotif] = useState("");
  const isDir = ["super_admin", "direction_generale"].includes(role);
  const close = async () => {
    if (!window.confirm("Clôturer définitivement ce dossier technique ?")) return;
    try { await api.post(`/technique/files/${file.id}/close`, { motif }); toast.success("Dossier clôturé"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (file.statut === "Clôturé") return (
    <div className="rounded-lg border bg-slate-50 p-4 text-sm" data-testid="tech-closed-info"><Lock className="inline h-4 w-4 mr-1 text-slate-500" />Clôturé le {fmtDate(file.closed_at)} par {file.closed_by}{file.close_forced ? ` — clôture forcée : ${file.close_motif}` : ""}</div>
  );
  const blocked = !hasDefinitive || (openReserves > 0 && !isDir);
  return (
    <div className="rounded-lg border bg-white p-4 space-y-2" data-testid="tech-close-box">
      <p className="text-sm font-semibold">Clôture du dossier</p>
      {!hasDefinitive && <p className="text-xs text-slate-500">La réception définitive doit être prononcée avant la clôture.</p>}
      {hasDefinitive && openReserves > 0 && <p className="text-xs text-rose-600">{openReserves} réserve(s) encore ouverte(s). {isDir ? "En tant que direction, vous pouvez forcer la clôture avec un motif." : "Levez toutes les réserves pour clôturer."}</p>}
      {hasDefinitive && openReserves > 0 && isDir && <Textarea rows={2} placeholder="Motif de clôture forcée (obligatoire)" value={motif} onChange={(e) => setMotif(e.target.value)} data-testid="tech-close-motif" />}
      <Button disabled={blocked || (openReserves > 0 && motif.trim().length < 5)} onClick={close} variant="outline" className="border-rose-200 text-rose-700" data-testid="tech-close-btn"><Lock className="h-4 w-4 mr-1" />Clôturer le dossier</Button>
    </div>
  );
}

export default function TechReception({ file, suivi, reload }) {
  const [pv, setPv] = useState(null);
  const openReserves = suivi.reserves.filter((r) => r.statut !== "Levée").length;
  const hasDefinitive = suivi.receptions.some((r) => r.type === "definitive");
  const closed = file.statut === "Clôturé";
  const pdf = async (r) => {
    try { const res = await api.get(`/technique/receptions/${r.id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        {[["1. Pré-réception", suivi.receptions.some((r) => r.type === "pre")], ["2. Réserves levées", openReserves === 0 && suivi.reserves.length > 0], ["3. Réception définitive", hasDefinitive]].map(([l, done]) => (
          <div key={l} className={`rounded-lg border p-3 ${done ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white text-slate-500"}`}>{l}{done ? " ✓" : ""}</div>))}
      </div>
      {!closed && <div className="flex gap-2 flex-wrap">
        <Button variant="outline" onClick={() => setPv("pre")} data-testid="pv-pre-btn"><FileCheck2 className="h-4 w-4 mr-1" />Pré-réception</Button>
        <Button disabled={hasDefinitive} onClick={() => setPv("definitive")} data-testid="pv-def-btn" className="bg-brand-600 hover:bg-brand-700 text-white"><FileCheck2 className="h-4 w-4 mr-1" />Réception définitive</Button>
      </div>}
      <div className="rounded-lg border bg-white overflow-hidden">
        {suivi.receptions.length === 0 ? <p className="px-4 py-3 text-sm text-slate-400">Aucun PV.</p> : <div className="divide-y">{suivi.receptions.map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 text-sm flex-wrap" data-testid={`pv-row-${r.id}`}>
            <span className="font-mono text-xs text-slate-500">{r.numero}</span>
            <span className="flex-1 font-medium">{LABEL[r.type]} · {fmtDate(r.date)} · {r.avec_reserves ? `avec ${r.reserves_snapshot.length} réserve(s)` : "sans réserve"}</span>
            <span className="text-xs text-slate-500">{r.sig_client_nom} / {r.sig_tech_nom}</span>
            <Button size="sm" variant="outline" onClick={() => pdf(r)} data-testid={`pv-pdf-${r.id}`}><FileDown className="h-4 w-4 mr-1" />PV PDF</Button>
          </div>))}</div>}
      </div>
      <CloseBox file={file} openReserves={openReserves} hasDefinitive={hasDefinitive} reload={reload} />
      {pv && <PvDialog type={pv} file={file} openReserves={openReserves} onClose={() => setPv(null)} onDone={() => { setPv(null); reload(); }} />}
    </div>
  );
}
